import CloseIcon from '@mui/icons-material/Close';
import { Box, Chip, FormHelperText, Stack, Typography } from '@mui/material';
import { first, map } from 'lodash';
import React, { ReactElement, useRef, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { getFilesPromiseFromDataTransferItemList } from 'utils/fileSystem';
import { IFormValues } from './UploadSlidesForm';

interface Props {
  formProps: UseFormReturn<IFormValues, any> | any;
}

const UploadFiles = ({ formProps }: Props): ReactElement => {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const {
    control,
    register,
    watch,
    formState: { errors },
    getValues,
    setValue,
  } = formProps;
  const selectedSlides = watch('slides') || [];

  // handle drag events
  const handleDrag = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // triggers when file is dropped
  const handleDrop = async (e: React.DragEvent<HTMLDivElement>, field: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const items: DataTransferItemList = e.dataTransfer.items;
    getFilesPromiseFromDataTransferItemList(items).then((files: File[]) => {
      if (files) {
        const newFiles: File[] = [];
        for (const curFile of files) {
          if (!field.value.some((file: File) => file.name === curFile.name)) {
            newFiles.push(curFile);
          }
        }
        field.onChange([...field.value, ...newFiles]);
      }
    });
  };

  // triggers when file is selected with click
  const handleChange = (e: any, field: any) => {
    e.preventDefault();
    if (e.target.files && first(e.target.files)) {
      field.onChange([...field.value, ...e.target.files]);
    }
  };

  // triggers the input when the button is clicked
  const onButtonClick = () => {
    inputRef.current?.click();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        px: 3,
        py: 2,
      }}
    >
      <Controller
        name="slides"
        control={control}
        defaultValue={[]}
        render={({ field }) => {
          const registeredSlides = register('slides');

          return (
            <Box
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={(event) => {
                handleDrop(event, field);
              }}
            >
              <Box
                component="input"
                {...registeredSlides}
                ref={(element: HTMLInputElement | null) => {
                  registeredSlides.ref(element);
                  inputRef.current = element;
                }}
                type="file"
                id="input-file-upload"
                multiple
                tabIndex={-1}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleChange(event, field);
                }}
                sx={{
                  display: 'none',
                }}
              />
              <Box
                component="div"
                role="button"
                tabIndex={0}
                aria-label="Drag and drop your slides or browse"
                onClick={onButtonClick}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onButtonClick();
                  }
                }}
                sx={(theme) => ({
                  width: '100%',
                  minHeight: 72,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.75,
                  border: 1,
                  borderStyle: 'dashed',
                  borderColor: dragActive ? 'primary.main' : 'divider',
                  borderRadius: theme.shape.borderRadius,
                  bgcolor: dragActive ? 'action.selected' : 'background.default',
                  color: 'text.secondary',
                  cursor: 'pointer',
                  transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow']),
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover',
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                  },
                })}
              >
                <Typography variant="body2" component="span">
                  Drag and drop your slides
                </Typography>
                <Typography variant="body2" component="span" color="primary">
                  or browse
                </Typography>
              </Box>
            </Box>
          );
        }}
      />
      {errors.slides && <FormHelperText error>{errors.slides.message}</FormHelperText>}

      {selectedSlides.length > 0 && !errors.slides && (
        <Stack
          direction="row"
          flexWrap="wrap"
          useFlexGap
          spacing={1}
          data-testid="selected-slide-file-list"
          aria-label="Selected slide files"
          sx={{ maxHeight: { xs: 160, md: 220 }, overflowY: 'auto', pr: 0.5 }}
        >
          {map(selectedSlides, (slide: File, index: number) => (
            <Chip
              key={slide.name}
              label={slide.name}
              size="small"
              variant="outlined"
              deleteIcon={<CloseIcon />}
              onDelete={() => {
                const newSlides = [...getValues('slides')];
                newSlides.splice(index, 1);
                setValue('slides', newSlides);
              }}
              sx={{ maxWidth: 320 }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default UploadFiles;
